// Shared rich-text rendering pipeline (Alex 2026-06-20: extracted from
// AgentDetail so the collab group-chat bubbles render IDENTICALLY to agent
// replies — Markdown→HTML, ```mermaid fences → SVG via beautiful-mermaid,
// storage-media links → StorageMedia, trusted frontends → inline iframe).
//
// AgentDetail keeps its own <Markdown> call-sites (it pre-processes per
// section kind), but imports the pieces from here so there is ONE
// mdComponents map. New consumers (CollabView) use <RichMessage>, the
// convenience wrapper that does stripAnsi + autoWrapMermaid + <Markdown>.
import { Children, isValidElement, type ReactNode } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { MermaidBlock, autoWrapMermaid } from './agentMermaid';
import { linkifyPostRefs } from '../lib/postRefs';
import { serviceOrigin } from '../lib/services';
import { StorageMedia } from './StorageMedia';
import { AudioBlock } from './AudioBlock';
import { SmartTable } from './SmartTable';
// The media/embed CSS classes (inline-media-*, content-post-embed,
// v2-content-embed, v2-mermaid) live in AgentDetail.css. CSS imports are a
// global side-effect in Vite, so importing here guarantees the classes
// exist wherever RichMessage is used — even if AgentDetail never mounted.
import './AgentDetail.css';

// Plugin tuples extracted to module scope so React doesn't see a fresh
// array reference on every render — without this, Markdown's internal
// memoisation thinks the pipeline changed and re-parses each tick.
export const REMARK_PLUGINS = [remarkGfm, remarkMath];
export const REHYPE_PLUGINS = [rehypeKatex];

export const IMAGE_EXTS = /\.(png|jpg|jpeg|webp|gif|svg|bmp)(\?|$)/i;
export const AUDIO_EXTS = /\.(mp3|wav|ogg|m4a|aac|flac)(\?|$)/i;
export const VIDEO_EXTS = /\.(mp4|webm|mov)(\?|$)/i;
// Matches all Storage-API hosts: arkturian (Linode), arkserver (the active
// transcoding host with X-HLS-URL headers), and federation-mounted
// storage-api on the oneal node (gsgbot.arkturian.com/storage-api).
export const STORAGE_MEDIA = /(?:api-storage(?:\.arkserver)?\.arkturian\.com|gsgbot\.arkturian\.com\/storage-api)\/storage\/media\//;

// Content-API post permalinks → inline embed via /embed/post/{id}. The
// embed route internally calls GET /api/v1/posts/{id} which ONLY accepts
// numeric ids (422 on slugs — Content confirmed in IACP bb139c98). So we
// require a numeric id to engage the compact ContentPostEmbed. Slug
// permalinks fall through to GenericContentEmbed (iframes the regular
// post page, which works) so the user still sees something.
const CONTENT_POST_URL = /^https?:\/\/content2?\.arkturian\.com\/posts?\/(\d+)\b/i;
function extractPostId(url: string): string | null {
  const m = url.match(CONTENT_POST_URL);
  return m ? m[1] : null;
}

// Trusted-host patterns for standalone-URL iframe embedding (our own
// frontends only — never embed external sites).
const TRUSTED_EMBED_PATTERNS: RegExp[] = [
  /^content\.arkturian\.com$/i,
  /^cloud2?\.arkturian\.com$/i,
  /^[a-z0-9-]+\.arkturian\.com$/i,
  /^[a-z0-9-]+\.arkserver\.arkturian\.com$/i,
];

function isTrustedEmbedUrl(href: string): boolean {
  try {
    const u = new URL(href);
    if (u.protocol !== 'https:') return false;
    return TRUSTED_EMBED_PATTERNS.some((p) => p.test(u.host));
  } catch {
    return false;
  }
}

// Append ?embed=1 as a hint for trusted frontends to hide chrome.
// Frontends that don't honor it just render normally — still works.
function withEmbedQuery(href: string): string {
  try {
    const u = new URL(href);
    if (!u.searchParams.has('embed')) u.searchParams.set('embed', '1');
    return u.toString();
  } catch {
    return href;
  }
}

// Defensive: backend sometimes leaks OSC-8 hyperlink escape residue into URLs
// (e.g. "...media/4451\\...]8;;\\"). Strip everything after first backslash
// and any trailing OSC-8 marker so the URL becomes valid again.
function cleanUrl(url: string): string {
  let u = url.split('\\')[0];
  u = u.replace(/\]8;;.*$/, '');
  return u;
}

// Strip leaked terminal escape sequences from rendered text.
// Pattern: full OSC-8 ESC]8;id=…;URL BEL TEXT ESC]8;; BEL.
// Backend partially strips but leaves visible residue ("☒]8;id=xxx;"
// or "]8;;\"). We clean all variants client-side.
// eslint-disable-next-line no-control-regex
const ANSI_CSI_RE = /\x1b\[[0-9;]*[a-zA-Z]/g;
// eslint-disable-next-line no-control-regex
const OSC_FULL_RE = /\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)/g;
const OSC_RESIDUE_RE = /\]8;[^;\n]*;[^\s\\\n]*\\?/g;
// eslint-disable-next-line no-control-regex
const BEL_RE = /\x07/g;

export function stripAnsi(text: string): string {
  let out = text.replace(ANSI_CSI_RE, '');
  out = out.replace(OSC_FULL_RE, '');
  out = out.replace(OSC_RESIDUE_RE, '');
  out = out.replace(BEL_RE, '');
  return out;
}

function isImageUrl(url: string): boolean {
  // Explicit extension only — Storage-API URLs without ext go through
  // StorageMedia (HEAD-detect) so videos can route to the HLS player
  // instead of being forced into an <img> tag.
  return IMAGE_EXTS.test(url);
}

function isAudioUrl(url: string): boolean {
  return AUDIO_EXTS.test(url);
}

function isVideoUrl(url: string): boolean {
  return VIDEO_EXTS.test(url);
}

export function storageMediaUrl(url: string): string {
  if (STORAGE_MEDIA.test(url) && !url.includes('format=')) {
    // variant=large + webp: more source pixels per displayed pixel
    // (less aliasing on retina) and DCT-free compression that doesn't
    // amplify fine parallel lines into moiré patterns. JPEG fallback
    // happens server-side if the source isn't webp-encodable.
    return url + (url.includes('?') ? '&' : '?') + 'format=webp&variant=large';
  }
  return url;
}

const mdCode = (props: { className?: string; children?: React.ReactNode }) => {
  const m = /language-(\w+)/.exec(props.className || '');
  if (m?.[1] === 'mermaid') {
    return <MermaidBlock source={String(props.children || '')} />;
  }
  return <code className={props.className}>{props.children}</code>;
};

// ReactMarkdown wraps fenced code in <pre><code>. For mermaid we
// already render a <div class="v2-mermaid"> inside that <code>, so the
// surrounding <pre> just adds the chrome we don't want. Unwrap when the
// child is a mermaid code block; otherwise let the default <pre> render.
const mdPre = (props: { children?: React.ReactNode }) => {
  const child = Array.isArray(props.children) ? props.children[0] : props.children;
  const className =
    (child && typeof child === 'object' && 'props' in child
      ? ((child as { props?: { className?: string } }).props?.className || '')
      : '');
  if (/language-mermaid\b/.test(className)) {
    return <>{props.children}</>;
  }
  return <pre {...props} />;
};

function ContentPostEmbed({ postId, fullUrl }: { postId: string; fullUrl: string }) {
  // Die Beitrags-OBERFLÄCHE, nicht die API. Auf einer Kundeninstanz sind das
  // zwei verschiedene Adressen (Davids `content` ist der API-Durchgriff, dort
  // antwortet JSON). Meldet die Instanz keine Oberfläche, bleibt der Verweis
  // ein Link — eine Einbettung ins Leere ist schlechter als ein Link.
  const webOrigin = serviceOrigin('content_web');
  if (!webOrigin) {
    return <a href={fullUrl} target="_blank" rel="noopener noreferrer">Beitrag #{postId}</a>;
  }
  const embedSrc = `${webOrigin}/embed/post/${postId}`;
  return (
    <div className="content-post-embed">
      <iframe
        src={embedSrc}
        loading="lazy"
        title={`Content post ${postId}`}
        className="content-post-iframe"
      />
      <a
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="content-post-open"
        title="Open full post"
      >
        ↗
      </a>
    </div>
  );
}

// Generic trusted-URL embed for standalone links to our own frontends
// (cloud.*, *.arkturian.com, *.arkserver.arkturian.com, …). Larger than
// ContentPostEmbed because the target page is a full app view, not just
// a single post. Sandboxed but with same-origin + scripts so the target
// frontend can boot.
function GenericContentEmbed({ href, label }: { href: string; label: string }) {
  return (
    <div className="v2-content-embed">
      <iframe
        src={withEmbedQuery(href)}
        title={label}
        loading="lazy"
        sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-forms"
        className="v2-content-embed-iframe"
      />
      <div className="v2-content-embed-caption">
        embed:{' '}
        <a href={href} target="_blank" rel="noopener noreferrer">
          {href}
        </a>
      </div>
    </div>
  );
}

// Standalone-URL detection: a paragraph that contains exactly one
// meaningful child which is an <a> to a trusted host → render as
// inline iframe instead of a blue link. Inline links inside prose
// stay as regular links (the multi-child check excludes those).
const mdParagraph = (props: { children?: React.ReactNode }) => {
  const raw = Array.isArray(props.children) ? props.children : [props.children];
  const meaningful = raw.filter(
    (c) => c !== null && c !== undefined && !(typeof c === 'string' && /^\s*$/.test(c))
  );
  if (meaningful.length === 1) {
    const only = meaningful[0] as { props?: { href?: string; children?: React.ReactNode } } | null;
    const href = only?.props?.href;
    if (href && isTrustedEmbedUrl(href)) {
      // Media URLs (image / audio / video / storage-media on either node)
      // belong to the <a> override — it renders them as <img>, <audio>,
      // <video> or StorageMedia. Falling through to default <p> here lets
      // ReactMarkdown call mdComponents.a with the right handler. Without
      // this guard, mdParagraph wrapped the image as a GenericContentEmbed
      // iframe (Alex saw the OnealSearch helmet photo land in a 720×560
      // frame instead of inline).
      if (
        isImageUrl(href) ||
        isAudioUrl(href) ||
        isVideoUrl(href) ||
        STORAGE_MEDIA.test(href)
      ) {
        return <p>{props.children}</p>;
      }
      // Content-posts get the compact /embed/post/{id} treatment.
      const postId = extractPostId(href);
      if (postId) {
        return <ContentPostEmbed postId={postId} fullUrl={href} />;
      }
      const childText = only?.props?.children;
      const label = typeof childText === 'string' ? childText : href;
      return <GenericContentEmbed href={href} label={label} />;
    }
  }
  return <p>{props.children}</p>;
};

// Pull the cell CONTENT (as ReactNode, so bold/code/links survive) out of a
// react-markdown <tr>'s <th>/<td> children.
function rowCells(tr: ReactNode): ReactNode[] {
  const cells: ReactNode[] = [];
  if (!isValidElement(tr)) return cells;
  Children.forEach((tr.props as { children?: ReactNode }).children, (cell) => {
    if (isValidElement(cell)) cells.push((cell.props as { children?: ReactNode }).children);
  });
  return cells;
}

// Turn react-markdown's <table> children (<thead>/<tbody>) into {headers, rows}
// for SmartTable — which offers the Tabelle/Karten toggle (#4231-style mobile).
function extractTable(children: ReactNode): { headers: ReactNode[]; rows: ReactNode[][] } | null {
  let headers: ReactNode[] = [];
  const rows: ReactNode[][] = [];
  Children.forEach(children, (part) => {
    if (!isValidElement(part)) return;
    const inner = (part.props as { children?: ReactNode }).children;
    if (part.type === 'thead') {
      Children.forEach(inner, (tr) => { if (isValidElement(tr)) headers = rowCells(tr); });
    } else if (part.type === 'tbody') {
      Children.forEach(inner, (tr) => { if (isValidElement(tr)) rows.push(rowCells(tr)); });
    }
  });
  if (headers.length === 0 && rows.length === 0) return null;
  return { headers, rows };
}

export const mdComponents = {
  code: mdCode,
  pre: mdPre,
  p: mdParagraph,
  table: (props: { children?: ReactNode }) => {
    const parsed = extractTable(props.children);
    if (parsed) return <SmartTable headers={parsed.headers} rows={parsed.rows} />;
    return <table>{props.children}</table>;
  },
  a: (props: React.AnchorHTMLAttributes<HTMLAnchorElement>) => {
    const href = cleanUrl(props.href || '');
    if (isImageUrl(href)) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer">
          <img src={storageMediaUrl(href)} alt="" className="inline-media-img" />
        </a>
      );
    }
    if (isAudioUrl(href)) {
      return <AudioBlock src={href} />;
    }
    if (isVideoUrl(href)) {
      return <video controls src={href} className="inline-media-video" playsInline />;
    }
    if (STORAGE_MEDIA.test(href)) {
      return <StorageMedia url={href} />;
    }
    const postId = extractPostId(href);
    if (postId) {
      // NACKTE URL = Einbettung (der Operator hat den Beitrag hergezeigt),
      // BENANNTER Link = Erwähnung → kompakter Chip. Ohne diese Unterscheidung
      // würde jede inline-Erwähnung („Post #4754" im Satz) einen iframe über
      // den halben Verlauf ziehen (Alex 17.08.).
      const kids = props.children;
      const label = typeof kids === 'string'
        ? kids
        : Array.isArray(kids) && typeof kids[0] === 'string' ? kids[0] : '';
      if (!label || label.trim() === href.trim()) {
        return <ContentPostEmbed postId={postId} fullUrl={href} />;
      }
      return (
        <a className="post-ref-link" href={href} target="_blank" rel="noopener noreferrer" title={`Beitrag ${postId} öffnen`}>
          {kids}
        </a>
      );
    }
    return <a {...props} href={href} target="_blank" rel="noopener noreferrer" />;
  },
  img: (props: React.ImgHTMLAttributes<HTMLImageElement>) => (
    <img {...props} className="inline-media-img" />
  ),
};

/**
 * Render arbitrary agent/collab text with the full pipeline — the same
 * Markdown + mermaid + media handling agent replies get. Use this anywhere
 * a message body should look like a proper agent answer (collab bubbles,
 * question banners, future task/vote/product cards).
 */
export function RichMessage({ text, className }: { text: string; className?: string }) {
  const content = linkifyPostRefs(autoWrapMermaid(stripAnsi(text || '')), serviceOrigin('content_web'));
  return (
    <div className={className}>
      <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={mdComponents}>
        {content}
      </Markdown>
    </div>
  );
}
